import { Link } from "@tanstack/react-router";
import type { IdentityProps } from "@totalenergiescode/mobility-business-rev-design-system";
import {
  Divider,
  Text,
} from "@totalenergiescode/mobility-business-rev-design-system";
import React from "react";
import { useTranslation } from "react-i18next";

import { useGetAccountDetail } from "@/api/graphql/accounts/getAccountDocument/hooks.ts";
import { useBreadCrumbItems } from "@/components/Accounts/details/hooks.ts";
import { AccountsDetailsTid } from "@/components/Accounts/details/tid/component";
import { AgentActionsList } from "@/components/actions/agents/component.tsx";
import { Loading } from "@/components/shared/Loading/component";
import { UsersList } from "@/components/Users/list/component";
import { useFeatureFlags } from "@/contexts/featureFlags/context";
import { toFullName } from "@/helpers/identity/toFullName/helper";
import { useMe } from "@/hooks/session/useMe/hook";

import {
  StyledAddressContainerDiv,
  StyledArrowRight,
  StyledButtonHeaderContainerDiv,
  StyledCard,
  StyledCardContainerDiv,
  StyledCardContentText,
  StyledCardTitleText,
  StyledContainerDiv,
  StyledIdentity,
  StyledNotificationsButton,
  StyledPageHeader,
  StyledRoundedTopRightCardDiv,
  StyledSectionContainerDiv,
  StyledTextContainerDiv,
  StyledTitleText,
} from "./styles";

export const AccountDetails: React.FC<{ id: string }> = ({ id }) => {
  const { t: tCompanies } = useTranslation("companies");
  const { t: tNotifications } = useTranslation("notifications");
  const { data: meData, isLoading: isMeLoading } = useMe();
  const { companyData, isLoading } = useGetAccountDetail(id);
  const fullName = toFullName({
    firstName: companyData?.userFirstName,
    lastName: companyData?.userLastName,
  });
  const fullNameAvatar =
    companyData?.userFirstName + " " + companyData?.userLastName || "N F";
  const avatarData: IdentityProps["avatar"] = {
    firstName: companyData?.userFirstName ?? "",
    lastName: companyData?.userLastName,
    type: "initials",
  };
  const addressDetail = `${companyData?.zipCode} ${companyData?.city} - ${companyData?.country}`;
  const breadcrumbItems = useBreadCrumbItems(companyData?.name);
  const isSuperAgent =
    meData?.AccountsMsMe.supportUserInfo.rights?.includes("SUPER_AGENT");

  const { isTidEnabled } = useFeatureFlags();
  const hasDownloadInvoiceFilesTidVisaFeature =
    companyData?.enabledFeatures?.includes("DOWNLOAD_INVOICE_FILES_TID");
  const canDisplayTidDetails =
    isTidEnabled && hasDownloadInvoiceFilesTidVisaFeature;

  return (
    <StyledContainerDiv>
      <Loading isLoading={isLoading}>
        <>
          {companyData?.name && (
            <StyledPageHeader
              breadcrumbItems={breadcrumbItems}
              title={companyData?.name}
              href={""}
              rightContent={
                <StyledButtonHeaderContainerDiv>
                  <Link
                    to='/account/$id/notifications'
                    params={{ id: id || "" }}
                  >
                    <StyledNotificationsButton
                      onClick={() => {}}
                      buttonText={tNotifications(
                        "notifications.account.button.name",
                      )}
                      variant={"primary"}
                      isLowercase
                      leftIcon={<StyledArrowRight />}
                      size={"m"}
                    />
                  </Link>
                </StyledButtonHeaderContainerDiv>
              }
            />
          )}
          <StyledSectionContainerDiv>
            <StyledTitleText variant='title2'>
              {tCompanies("companies.list.subtitles.info")}
            </StyledTitleText>
            <StyledCardContainerDiv>
              <StyledCard>
                <StyledCardTitleText variant='title2'>
                  {tCompanies("companies.cards.companyName")}
                </StyledCardTitleText>
                <Divider />
                <StyledCardContentText variant='title2'>
                  {companyData?.name}
                </StyledCardContentText>
              </StyledCard>
              <StyledRoundedTopRightCardDiv>
                <StyledCardTitleText variant='title2'>
                  {tCompanies("companies.cards.accountOwner")}
                </StyledCardTitleText>
                <Divider />
                <StyledIdentity
                  avatar={avatarData}
                  subtitle={companyData?.userEmail}
                  title={fullNameAvatar}
                />
              </StyledRoundedTopRightCardDiv>
              <StyledCard>
                <StyledCardTitleText variant='title2'>
                  {tCompanies("companies.cards.accountNumber")}
                </StyledCardTitleText>
                <Divider />
                <StyledCardContentText variant='title2'>
                  {companyData?.number}
                </StyledCardContentText>
              </StyledCard>
              <StyledCard>
                <StyledCardTitleText variant='title2'>
                  {tCompanies("companies.cards.officeAddress")}
                </StyledCardTitleText>
                <Divider />
                <StyledAddressContainerDiv>
                  <StyledTextContainerDiv>
                    <Text
                      variant='text2'
                      dataTestId='order__order-ledgers-confirmation-company-name_text'
                    >
                      {companyData?.name}
                    </Text>
                    <Text variant='text2'>{fullName}</Text>
                  </StyledTextContainerDiv>
                  <StyledTextContainerDiv>
                    <Text variant='text2'>{companyData?.address}</Text>
                    <Text variant='text2'>{addressDetail}</Text>
                  </StyledTextContainerDiv>
                </StyledAddressContainerDiv>
              </StyledCard>
            </StyledCardContainerDiv>
          </StyledSectionContainerDiv>
          <UsersList accountId={id} />
          {canDisplayTidDetails && <AccountsDetailsTid accountId={id} />}
          <Loading isLoading={isMeLoading}>
            <>{isSuperAgent && <AgentActionsList accountId={id} />}</>
          </Loading>
        </>
      </Loading>
    </StyledContainerDiv>
  );
};
